import { createTheme, Theme } from "@mui/material/styles";
import { creditStatus } from "./util/creditRequestMapping";
import { creditRequestStatusToColour } from "./util/creditRequestStatusMapping";
import type { ThemeMode } from "./providers/theme-mode/ThemeModeContext";

// Augment @mui/styles DefaultTheme to use MUI's Theme
declare module "@mui/styles/defaultTheme" {
	// eslint-disable-next-line @typescript-eslint/no-empty-interface
	interface DefaultTheme extends Theme {}
}

// Disable CSS variables globally for MUI 6 compatibility with @mui/styles
declare module '@mui/material/styles' {
	interface ThemeVars {
		// Empty interface to prevent CSS variable generation
	}
}

declare module "@mui/material/styles" {
	interface CommonColors {
		green: string;
	}

	interface Palette {
		custom: Record<string, any>;
	}

	interface PaletteOptions {
		custom?: Record<string, any>;
	}

	interface SimplePaletteColorOptions {
		fontColor?: string;
	}

	interface PaletteColor {
		fontColor?: string;
	}

	interface Theme {
		fullWidth: {
			width: string;
		};
		fullHeight: {
			height: string;
		};
		backDrop: {
			zIndex: number;
		};
	}

	interface ThemeOptions {
		fullWidth?: {
			width: string;
		};
		fullHeight?: {
			height: string;
		};
		backDrop?: {
			zIndex: number;
		};
	}
}

const jbColour = {
	yellow: "#ffec0f",
	orange: {
		primary: "#de8803",
		border: "#da6a00",
		background: "#fff9f0",
	},
	grey: {
		light: "#ebebeb",
	},
	blue: {
		border: "#095CA0",
		background: "#E8FAFF",
		bright: "#1890FF",
	},
	green: {
		primary: "#028702",
		border: "#028702",
		background: "#F2F9F2",
	},
	red: {
		primary: "#ff0000",
		border: "#e02020",
		background: "#fdf2f2",
	},
};

export function createAppTheme(mode: ThemeMode) {
	const isDark = mode === "dark";

	return createTheme({
		cssVariables: false,
		defaultColorScheme: mode,
		typography: {
			body1: {
				fontSize: "0.875rem",
				lineHeight: 1.43,
				letterSpacing: 0,
			},
			body2: {
				fontWeight: "bold",
				letterSpacing: 0,
			},
			caption: {
				letterSpacing: 0,
			},
		},
		palette: {
			mode,
			...(isDark ? {} : { background: { default: "#fafafa" } }),
			action: {
				active: isDark ? "rgba(255, 255, 255, 0.56)" : "rgba(0, 0, 0, 0.87)",
			},
			common: {
				green: "#028702",
			},
			primary: {
				main: "#000000",
				light: "#cccccc",
				dark: "#000000",
				fontColor: "#ffffff",
			},
			secondary: {
				main: "#028702",
			},
			custom: {
				creditRequest: {
					default: isDark ? "rgba(255, 255, 255, 0.12)" : "#d3d4d5",
					fieldBackground: isDark ? "#2b2b2b" : "#ffffff",
					menuBorder: isDark ? "rgba(255, 255, 255, 0.12)" : "#d3d4d5",
					warningIcon: isDark ? "#ffa726" : jbColour.orange.border,
					inProgress: creditRequestStatusToColour(creditStatus.inProgress),
					draft: creditRequestStatusToColour(creditStatus.draft),
					awaitingSupplier: creditRequestStatusToColour(creditStatus.awaitingSupplier),
					pendingReview: creditRequestStatusToColour(creditStatus.pendingReview),
					reviewing: isDark ? "#546e7a" : creditRequestStatusToColour(creditStatus.reviewing),
					reviewingBadge: creditRequestStatusToColour(creditStatus.reviewing),
					cancelled: creditRequestStatusToColour(creditStatus.cancelled),
					claimed: creditRequestStatusToColour(creditStatus.claimed),
				},
				searchResults: {
					manualReceive: {
						color: "#ffffff",
						background: "#000000",
						note: "#ffec0f",
					},
				},
				jbAppBar: {
					background: "#ffec0f",
					color: "#000000",
				},
				warning: "#ffca28",
				error: "#f44336",
				indicator: isDark
					? {
							full: "#1b5e20",
							partial: "#827717",
							none: "#b71c1c",
							warn: "#bf360c",
							over: "#e65100",
							mismatch: "#6a1b9a",
						}
					: {
							full: "#e6ffcc",
							partial: "lightyellow",
							none: "#ffcccc",
							warn: "#ff5722",
							over: "#ffac33",
							mismatch: "#ae76d3",
						},
				border: {
					full: "green",
					partial: "#FFCF44",
					none: "red",
					warn: "#ff5722",
					over: "#ffac33",
					grey: "#999999",
					mismatch: "#ae76d3",
				},
				shadow: {
					grey: isDark ? "#404040" : "#e1e2e3",
				},
				icon: {
					red: "#fa878d",
					darkRed: "#f44336",
					orange: "#fcac32",
					lightGreen: "#9bf779",
					darkGreen: "#299600",
				},
				badge: {
					padding: `0 10px 0 0`,
				},
				jbColours: {
					lightBlue: jbColour.blue.background,
					darkBlue: jbColour.blue.border,
					brightBlue: jbColour.blue.bright,
					lightRed: jbColour.red.background,
					darkRed: jbColour.red.border,
					lightGreen: jbColour.green.background,
					darkGreen: jbColour.green.border,
					lightOrange: jbColour.orange.background,
					darkOrange: jbColour.orange.border,
				},
				priorityFlagColors: {
					recall: "#6200EE",
					embargo: "#FD200E",
					newRelease: "#02C351",
					preOrder: "#FFEC10",
					highPriority: "#FF820F",
				},
			},
		},
		components: {
			MuiOutlinedInput: {
				styleOverrides: {
					notchedOutline: {
						...(isDark ? { borderColor: "rgba(255, 255, 255, 0.5)" } : {}),
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.Mui-focused": {
							...(isDark ? { color: jbColour.green.primary } : {}),
						},
					},
				},
			},
		},
		shape: {
			borderRadius: 0,
		},
		fullWidth: {
			width: "100%",
		},
		fullHeight: {
			height: "100%",
		},
		backDrop: {
			zIndex: 100,
		},
	});
}

export const theme = createAppTheme("light");
